import { useEffect, useState } from 'react';
import { SOCLOSE } from '../constants/soclose';

const STORAGE_KEY = 'soclose_cookie_consent_v1';

// PDPA-compliant cookie consent banner.
// Distinguishes essential / analytics / marketing cookies and stores the user's
// granular choice so we can re-check it elsewhere in the app via getCookieConsent().
export default function CookieConsent({ privacyPath = '/privacy', cookiesPath = '/cookies' }) {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({
    essential: true,    // always on, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setVisible(true);
      } else {
        setPrefs({ ...JSON.parse(stored), essential: true });
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const save = (choice) => {
    const payload = { ...choice, essential: true, ts: Date.now() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
    setPrefs(payload);
    setVisible(false);
    window.dispatchEvent(new CustomEvent('soclose:cookie-consent', { detail: payload }));
  };

  const acceptAll = () => save({ analytics: true, marketing: true });
  const rejectAll = () => save({ analytics: false, marketing: false });
  const saveCurrent = () => save({ analytics: prefs.analytics, marketing: prefs.marketing });

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-consent-text"
      className="fixed inset-x-0 bottom-0 z-[9999] p-4 md:p-6"
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-ef-border bg-ef-panel/95 p-5 shadow-2xl backdrop-blur-xl md:p-6">
        {!showSettings ? (
          <>
            <h2 className="mb-1 text-base font-semibold text-ef-text">Cookies & privacy</h2>
            {/* Sur telephone ce paragraphe occupait les deux tiers de l'ecran et
                recouvrait le formulaire de connexion: la premiere chose que voyait
                un nouvel utilisateur etait un pave juridique, pas l'application.
                Le texte n'est PAS tronque -- il reste entier, dans un cadre
                defilable -- pour que le consentement porte sur ce qui est ecrit.
                Seule sa hauteur est bornee, et les deux boutons redeviennent
                visibles sans faire defiler la page. */}
            <p id="cookie-consent-text" className="mb-4 max-h-28 overflow-y-auto text-sm leading-relaxed text-ef-textDim sm:max-h-none sm:overflow-visible">
              We use cookies to keep this site secure, understand how visitors use it, and improve our
              services. Essential cookies are always on. You can accept all, reject non-essential, or
              customize your choices. Your data is processed in accordance with the{' '}
              <a href={privacyPath} className="text-ef-accent underline-offset-2 hover:underline">Privacy Policy</a>{' '}
              of <strong>{SOCLOSE.name}</strong> ({SOCLOSE.uenLabel}, Singapore), under the {SOCLOSE.privacy.frameworkFull}.
            </p>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <button
                onClick={() => setShowSettings(true)}
                // py-2 lifts the 20px-tall text button above the WCAG 2.5.8
                // 24px tap-target floor without changing how it looks.
                className="py-2 text-sm text-ef-textMuted underline-offset-2 hover:text-ef-text hover:underline"
              >
                Customize settings
              </button>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  onClick={rejectAll}
                  className="rounded-lg border border-ef-border bg-ef-panel2 px-4 py-2 text-sm font-medium text-ef-textDim hover:text-ef-text"
                >
                  Reject non-essential
                </button>
                <button
                  onClick={acceptAll}
                  className="rounded-lg ef-gradient px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
                >
                  Accept all
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <h2 className="mb-3 text-base font-semibold text-ef-text">Cookie settings</h2>
            <div className="space-y-3 text-sm">
              <Row title="Essential" desc="Required for the site to work (login session, security)." enabled disabled />
              <Row
                title="Analytics"
                desc="Help us understand how the site is used (anonymized stats)."
                enabled={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })}
              />
              <Row
                title="Marketing"
                desc="Personalize content and measure campaign performance."
                enabled={prefs.marketing}
                onChange={(v) => setPrefs({ ...prefs, marketing: v })}
              />
            </div>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-between">
              <a href={cookiesPath} className="self-center py-2 text-sm text-ef-textMuted underline-offset-2 hover:text-ef-text hover:underline">
                Cookie policy
              </a>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  onClick={() => setShowSettings(false)}
                  className="rounded-lg border border-ef-border bg-ef-panel2 px-4 py-2 text-sm font-medium text-ef-textDim hover:text-ef-text"
                >
                  Back
                </button>
                <button
                  onClick={saveCurrent}
                  className="rounded-lg ef-gradient px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
                >
                  Save choices
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ title, desc, enabled, disabled, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-ef-border bg-ef-panel2 p-3">
      <div>
        <div className="font-medium text-ef-text">{title}</div>
        <div className="mt-0.5 text-xs text-ef-textMuted">{desc}</div>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange?.(!enabled)}
        disabled={disabled}
        aria-pressed={enabled}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
          enabled ? 'bg-ef-accent' : 'bg-ef-panel3'
        } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
            enabled ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  );
}

// Helper: read current consent elsewhere in app (e.g. analytics init).
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
